import { Box, SelectChangeEvent, Stack, Typography } from '@mui/material'
import { DeepcellDialog, DeepcellPrimarySelect, DeepcellTextField } from 'components/shared'
import useFlagCondition from 'components/shared/useFlagCondition'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useNotificationSlice } from 'redux/slices/hooks'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import useEventsManager from 'redux/slices/hooks/useEventsManager'
import { getSession, SamplingStrategy, VisualizationSessionConfig } from 'utils/api'
import useSessionApi from '../useSessionApi'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'

interface TuneHyperparametersDialogProps {
  isOpen: boolean
  onClose: () => void
}

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 20 * 60 * 1000

export function TuneHyperparametersDialog({
  isOpen,
  onClose,
}: TuneHyperparametersDialogProps): JSX.Element {
  const {
    cellVisualizations: { sessionData },
    setIsDirty,
  } = useCellVisualizationsSlice()
  const { createSessionFromRuns } = useSessionApi()
  const { updateSessionAndVersion } = useCellVisualizationUrlParams()
  const { showProgress, showError, showSuccess } = useNotificationSlice()
  const queryClient = useQueryClient()
  const eventsManager = useEventsManager()
  const tsneEnabled = useFlagCondition('cellVisualizationsEnableTSNE')

  const createSpec = sessionData?.create_spec

  const [sessionName, setSessionName] = useState('')
  const [projectionType, setProjectionType] = useState<'UMAP' | 'tSNE'>('UMAP')
  const [leidenResolution, setLeidenResolution] = useState('0.5')
  const [nNeighbors, setNNeighbors] = useState('15')
  const [minDist, setMinDist] = useState('0.1')
  const [spread, setSpread] = useState('1')
  const [perplexity, setPerplexity] = useState('30')
  const [earlyExaggeration, setEarlyExaggeration] = useState('12')
  const [perplexityError, setPerplexityError] = useState('')
  const [exaggerationError, setExaggerationError] = useState('')

  const [newSessionId, setNewSessionId] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pollingStartTime, setPollingStartTime] = useState(0)
  // After polling completes and navigation fires, hold the target session ID here
  // so we can keep the dialog open until the new session's data is fully loaded.
  const [navigatingToSessionId, setNavigatingToSessionId] = useState(0)

  const queryKey = useMemo(() => ['getTunedSession', newSessionId], [newSessionId])

  // Close the dialog only after the new session's data has been loaded into the store.
  // useLoadCellInfoEffect fetches the arrow data asynchronously after navigation, so we
  // watch sessionData.session_id and finish here once it matches the target session.
  useEffect(() => {
    if (!navigatingToSessionId) return
    if (Number(sessionData?.session_id) !== navigatingToSessionId) return

    setIsDirty(false)
    showSuccess('New session ready!')
    setNavigatingToSessionId(0)
    onClose()
  }, [navigatingToSessionId, sessionData?.session_id, setIsDirty, showSuccess, onClose])

  // Initialize form from current session's create_spec when dialog opens;
  // intentionally excludes createSpec/sessionData from deps to only run on open/close transitions
  useEffect(() => {
    if (!isOpen) {
      setNewSessionId(0)
      setIsSubmitting(false)
      setPollingStartTime(0)
      setNavigatingToSessionId(0)
      setPerplexityError('')
      setExaggerationError('')
      return
    }

    const method = createSpec?.projection_params?.method === 'tSNE' ? 'tSNE' : 'UMAP'
    setProjectionType(method)
    setLeidenResolution(String(createSpec?.leiden_resolution ?? 0.5))
    setNNeighbors(String(createSpec?.projection_params?.num_neighbors ?? 15))
    setMinDist(String(createSpec?.projection_params?.min_dist ?? 0.1))
    setSpread(String(createSpec?.projection_params?.spread ?? 1))
    setPerplexity(String(createSpec?.projection_params?.perplexity ?? 30))
    setEarlyExaggeration(String(createSpec?.projection_params?.exaggeration ?? 12))
    setSessionName(sessionData?.name ? `${sessionData.name} (tuned)` : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useQuery(queryKey, () => getSession(newSessionId, 1), {
    enabled: !!newSessionId,
    retry: false,
    refetchInterval: POLL_INTERVAL_MS,
    onError: (error: Error) => {
      setNewSessionId(0)
      setIsSubmitting(false)
      setPollingStartTime(0)
      showError(error.message)
    },
    onSuccess: (response) => {
      const status = response?.data?.data?.status

      if (status === 'ready') {
        const completedSessionId = newSessionId
        // Clean up polling state
        setNewSessionId(0)
        setIsSubmitting(false)
        setPollingStartTime(0)
        queryClient.cancelQueries(queryKey)
        // Navigate and keep dialog open until the new session's data is loaded
        // (useLoadCellInfoEffect fetches arrow data asynchronously after navigation)
        showProgress('Loading new session data...')
        updateSessionAndVersion(completedSessionId, 1)
        setNavigatingToSessionId(completedSessionId)
        return
      }

      if (status === 'failed') {
        setNewSessionId(0)
        setIsSubmitting(false)
        setPollingStartTime(0)
        showError('Session processing failed. Please try again with different parameters.')
        return
      }

      // Still processing — check for timeout
      if (pollingStartTime > 0 && Date.now() - pollingStartTime > POLL_TIMEOUT_MS) {
        setNewSessionId(0)
        setIsSubmitting(false)
        setPollingStartTime(0)
        showError('Session is taking longer than expected. It may still be processing — check the session list to see if it completes.')
        onClose()
        return
      }

      showProgress('Computing new session. This may take a few minutes...')
    },
  })

  const handleConfirm = useCallback(async () => {
    if (!sessionData?.runs) return
    setIsSubmitting(true)

    const config: VisualizationSessionConfig = {
      runs: sessionData.runs.map(({ run_id }) => ({ run_id })),
      morphometric_model: createSpec?.morphometric_model ?? '',
      dl_only_projection: true,
      max_cell_count: createSpec?.max_cell_count ?? 50000,
      name: sessionName,
      sampling_strategy: createSpec?.sampling_strategy ?? SamplingStrategy.PROPORTIONAL,
      leiden_resolution: parseFloat(leidenResolution),
      projection_params:
        projectionType === 'UMAP'
          ? {
              method: 'UMAP',
              num_neighbors: parseInt(nNeighbors, 10),
              min_dist: parseFloat(minDist),
              spread: parseFloat(spread),
            }
          : {
              method: 'tSNE',
              perplexity: parseInt(perplexity, 10),
              exaggeration: parseFloat(earlyExaggeration),
            },
    }

    eventsManager.sendCreateSesssionRequest(config)

    try {
      const result = await createSessionFromRuns(config)
      if (result?.data) {
        setNewSessionId(Number(result.data.session_id))
        setPollingStartTime(Date.now())
      } else {
        setIsSubmitting(false)
      }
    } catch {
      setIsSubmitting(false)
      showError('Error when attempting to create session')
    }
  }, [
    sessionData,
    createSpec,
    sessionName,
    leidenResolution,
    projectionType,
    nNeighbors,
    minDist,
    spread,
    perplexity,
    earlyExaggeration,
    eventsManager,
    createSessionFromRuns,
    showError,
  ])

  const handlePerplexityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      setPerplexity(value)
      const num = parseFloat(value)
      if (value !== '' && (Number.isNaN(num) || num <= 0)) {
        setPerplexityError('Perplexity must be a positive number greater than 0.')
      } else {
        setPerplexityError('')
      }
      eventsManager.sendProjectionParamChanged(projectionType, 'perplexity', value)
    },
    [eventsManager, projectionType]
  )

  const handleEarlyExaggerationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      setEarlyExaggeration(value)
      const num = parseFloat(value)
      if (value !== '' && (Number.isNaN(num) || num <= 0)) {
        setExaggerationError('Early Exaggeration must be a positive number greater than 0.')
      } else if (value !== '' && num > 32) {
        setExaggerationError('Early Exaggeration cannot exceed 32.')
      } else {
        setExaggerationError('')
      }
      eventsManager.sendProjectionParamChanged(projectionType, 'early_exaggeration', value)
    },
    [eventsManager, projectionType]
  )

  const hasValidationErrors = !!perplexityError || !!exaggerationError
  const isProcessing = isSubmitting || navigatingToSessionId > 0

  return (
    <DeepcellDialog
      open={isOpen}
      handleCancel={() => {
        if (!isProcessing) onClose()
      }}
      handleConfirm={handleConfirm}
      okLabel="Create New Session"
      okDisabled={!sessionName || isProcessing || hasValidationErrors}
      titleLabel="Tune Hyperparameters"
    >
      <Stack spacing={1.5} sx={{ width: 480, px: 1 }}>
        <DeepcellTextField
          label="Session Name"
          value={sessionName}
          onChange={(e) => setSessionName(e.target.value)}
          onFocus={(e) => e.target.select()}
          disabled={isProcessing}
        />

        <DeepcellPrimarySelect
          label="Projection Type"
          items={[
            { value: 'UMAP', output: 'UMAP' },
            ...(tsneEnabled ? [{ value: 'tSNE', output: 'tSNE' }] : []),
          ]}
          value={projectionType}
          onChange={(e: SelectChangeEvent<unknown>) => {
            const value = e.target.value as 'UMAP' | 'tSNE'
            setProjectionType(value)
            eventsManager.sendProjectionTypeSelected(value)
          }}
          disabled={isProcessing}
        />

        <DeepcellTextField
          label="Leiden Clustering Resolution"
          tooltip="Lower values create fewer clusters, and higher values create more clusters (default: 0.5)"
          type="number"
          value={leidenResolution}
          onChange={(e) => {
            setLeidenResolution(e.target.value)
            eventsManager.sendProjectionParamChanged(projectionType, 'leiden_resolution', e.target.value)
          }}
          inputProps={{ min: 0 }}
          size="small"
          margin="dense"
          onFocus={(e) => e.target.select()}
          disabled={isProcessing}
        />

        {projectionType === 'UMAP' ? (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.875rem', mb: 0.25 }}>
              UMAP Parameters
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <DeepcellTextField
                label="# of Neighbors"
                tooltip="Use a lower number to focus more on local structure, and higher number to focus more on global structure (default: 15)"
                type="number"
                value={nNeighbors}
                onChange={(e) => {
                  setNNeighbors(e.target.value)
                  eventsManager.sendProjectionParamChanged(projectionType, 'num_neighbors', e.target.value)
                }}
                inputProps={{ min: 1 }}
                size="small"
                margin="dense"
                sx={{ flex: 1 }}
                onFocus={(e) => e.target.select()}
                disabled={isProcessing}
              />
              <DeepcellTextField
                label="Minimum Distance"
                tooltip="Minimum distance between points in the layout. Smaller values will show finer structures and clustering, and bigger values will preserve more broad structure (default: 0.1)"
                type="number"
                value={minDist}
                onChange={(e) => {
                  setMinDist(e.target.value)
                  eventsManager.sendProjectionParamChanged(projectionType, 'min_dist', e.target.value)
                }}
                inputProps={{ min: 0, step: 0.1 }}
                size="small"
                margin="dense"
                sx={{ flex: 1 }}
                onFocus={(e) => e.target.select()}
                disabled={isProcessing}
              />
              <DeepcellTextField
                label="Spread"
                tooltip="Increase spread to increase global scaling (default: 1)"
                type="number"
                value={spread}
                onChange={(e) => {
                  setSpread(e.target.value)
                  eventsManager.sendProjectionParamChanged(projectionType, 'spread', e.target.value)
                }}
                inputProps={{ min: 0, step: 0.5 }}
                size="small"
                margin="dense"
                sx={{ flex: 1 }}
                onFocus={(e) => e.target.select()}
                disabled={isProcessing}
              />
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: '0.875rem', mb: 0.25 }}>
              tSNE Parameters
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <DeepcellTextField
                  label="Perplexity"
                  tooltip="Higher values will result in more global structure, and lower values will result in more local structure (default: 30)"
                  type="number"
                  value={perplexity}
                  onChange={handlePerplexityChange}
                  inputProps={{ min: 1 }}
                  size="small"
                  margin="dense"
                  onFocus={(e) => e.target.select()}
                  disabled={isProcessing}
                />
                {perplexityError && (
                  <Typography color="error" sx={{ fontSize: '0.95em', mt: 0.5 }}>
                    {perplexityError}
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <DeepcellTextField
                  label="Early Exaggeration"
                  tooltip="Controls how tight natural clusters in the original space are in the embedded space (default: 12, max: 32)"
                  type="number"
                  value={earlyExaggeration}
                  onChange={handleEarlyExaggerationChange}
                  inputProps={{ min: 1, max: 32 }}
                  size="small"
                  margin="dense"
                  onFocus={(e) => e.target.select()}
                  disabled={isProcessing}
                />
                {exaggerationError && (
                  <Typography color="error" sx={{ fontSize: '0.95em', mt: 0.5 }}>
                    {exaggerationError}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Stack>
    </DeepcellDialog>
  )
}

export default TuneHyperparametersDialog
